import { createSlice } from "@reduxjs/toolkit";
import { mapCadCategoriesFromApi } from "../mapCadCategories";
import {
  fetchCadCategories,
  submitCadWaterfall,
} from "../services/cadDiscountsThunk";
import {
  CadCategoriesApiResponse,
  CadCategoryDefinition,
  CadWaterfallApiResponse,
  CadwaterfallFullfilledPayload,
  ManageCADSaleItems,
  ManageCADSaleSummary,
} from "../types";
import {
  getSubmitWaterfallPricelistErrorMessage,
  isSubmitWaterfallPricelistError,
} from "dot/Utils/submitWaterfallPricelistError";


export interface CadDiscountsState {
  cadCategories: CadCategoriesApiResponse;
  categories: CadCategoryDefinition[];
  loading: boolean;
  error: string | null;
  waterfall: CadWaterfallApiResponse;
  waterfallLoading: boolean;
  waterfallError: string | null;
  saleItems: ManageCADSaleItems[];
  saleSummary: Partial<ManageCADSaleSummary>;
}

const initialState: CadDiscountsState = {
  cadCategories: null,
  categories: [],
  loading: false,
  error: null,
  waterfall: null,
  waterfallLoading: false,
  waterfallError: null,
  saleItems: [],
  saleSummary: {}
};

const cadDiscountsSlice = createSlice({
  name: "cadDiscounts",
  initialState,
  reducers: {
    resetCadDiscountsState: () => ({ ...initialState }),
    resetCadWaterfall: (state) => {
      state.waterfall = null;
      state.waterfallLoading = false;
      state.waterfallError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCadCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCadCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.cadCategories = action.payload;
        state.categories = mapCadCategoriesFromApi(action.payload);
      })
      .addCase(fetchCadCategories.rejected, (state, action) => {
        state.loading = false;
        state.categories = [];
        state.error =
          action.payload ||
          action.error.message ||
          "Failed to fetch CAD categories";
      })
      .addCase(submitCadWaterfall.pending, (state) => {
        state.waterfallLoading = true;
        state.waterfallError = null;
      })
      .addCase(submitCadWaterfall.fulfilled, (state, action) => {
        state.waterfallLoading = false;
        if (isSubmitWaterfallPricelistError(action.payload)) {
          state.waterfall = null;
          state.waterfallError = getSubmitWaterfallPricelistErrorMessage(
            action.payload,
          );
          return;
        }
        const payload = action.payload as
          | Partial<CadwaterfallFullfilledPayload>
          | null
          | undefined;
        state.saleItems = payload?.data?.items || [];
        state.saleSummary = payload?.data?.summary || {};
        state.waterfallError = null;
        state.waterfall = action.payload;
      })
      .addCase(submitCadWaterfall.rejected, (state, action) => {
        state.waterfallLoading = false;
        state.waterfallError =
          action.payload ||
          action.error.message ||
          "Failed to submit CAD waterfall";
      });
  },
});

export const { resetCadDiscountsState, resetCadWaterfall } =
  cadDiscountsSlice.actions;

export const selectCadCategories = (state: {
  cadDiscounts: CadDiscountsState;
}) => state.cadDiscounts.cadCategories;

export const selectCadCategoryDefinitions = (state: {
  cadDiscounts: CadDiscountsState;
}) => state.cadDiscounts.categories;

export const selectCadCategoriesLoading = (state: {
  cadDiscounts: CadDiscountsState;
}) => state.cadDiscounts.loading;

/** True until a response is mapped, or while a request is in flight. */
export const selectIsCadCategoriesLoading = (state: {
  cadDiscounts: CadDiscountsState;
}) =>
  state.cadDiscounts.loading ||
  (state.cadDiscounts.cadCategories === null &&
    state.cadDiscounts.error === null);

export const selectCadWaterfall = (state: { cadDiscounts: CadDiscountsState }) =>
  state.cadDiscounts.waterfall;

export const selectIsCadWaterfallLoading = (state: {
  cadDiscounts: CadDiscountsState;
}) =>
  state.cadDiscounts.waterfallLoading ||
  (state.cadDiscounts.waterfall === null &&
    state.cadDiscounts.waterfallError === null);

export default cadDiscountsSlice.reducer;
